import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  SetStateAction,
  Dispatch,
} from "react";
import { CellData, SpreadsheetData, Position } from "../types";
import { SparkleIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon, CircleNotchIcon, CheckCircleIcon, LightningIcon, UploadSimpleIcon, DownloadSimpleIcon, WarningCircleIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { ToastDetail } from "../App";
import ExamplePopup from "./ExamplePopup";
import ConfirmDialog from "./ConfirmDialog";
import CellHoverCard from "./CellHoverCard";
import UnifiedSourcesPanel from "./UnifiedSourcesPanel";
import { Source } from "./Tooltip";
import { exportToCSV } from "../utils";
import Papa from "papaparse";
import ExcelJS from "exceljs";

interface SpreadsheetProps {
  data: SpreadsheetData;
  setData: Dispatch<SetStateAction<SpreadsheetData>>;
  setToast: Dispatch<SetStateAction<ToastDetail>>;
}

// Production serves the UI and API from the same origin.
const API_URL =
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? "http://localhost:8000" : "");

// Keep in sync with MAX_ROWS and MAX_COLUMNS in application.py.
const MAX_ROWS = 20;
const MAX_COLUMNS = 20;
// Columns share the frame width evenly and scroll only below this width.
const MIN_COLUMN_WIDTH = 120;
const ROW_INDEX_WIDTH = 44;

// The server sends a keepalive every 15s, so this much silence means the stream died.
const IDLE_TIMEOUT_MS = 120_000;
const RETRY_HINT = "Finished cells were kept; click Retry failed to fill the rest.";

type StreamOutcome = "complete" | "error" | null;

// A cell is researched when its column has a header, it is empty, and its row has content.
const isTargetCell = (headers: string[], row: CellData[], colIndex: number) =>
  Boolean(headers[colIndex]?.trim()) &&
  !row[colIndex]?.value?.trim() &&
  row.some((cell) => cell.value?.trim());

const cellCount = (count: number) => `${count} ${count === 1 ? "cell" : "cells"}`;

const exceedsLimits = (table: SpreadsheetData) =>
  table.rows.length > MAX_ROWS || table.headers.length > MAX_COLUMNS;

const limitTable = (table: SpreadsheetData): SpreadsheetData => ({
  headers: table.headers.slice(0, MAX_COLUMNS),
  rows: table.rows.slice(0, MAX_ROWS).map((row) => row.slice(0, MAX_COLUMNS)),
});

const Spreadsheet: React.FC<SpreadsheetProps> = ({
  setToast,
  data,
  setData,
}) => {
  const [activeCell, setActiveCell] = useState<Position | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEnrichingTable, setIsEnrichingTable] = useState(false);
  const [enrichmentProgress, setEnrichmentProgress] = useState<string>("Starting research...");
  const [rowProgress, setRowProgress] = useState({ done: 0, total: 0 });
  const [isSourcesPanelOpen, setIsSourcesPanelOpen] = useState(false);
  const [collectedQueries, setCollectedQueries] = useState<string[]>([]);
  const [currentQueries, setCurrentQueries] = useState<string[]>([]);
  const [eventHistory, setEventHistory] = useState<Array<{type: string; message: string; timestamp: number; completed: boolean}>>([]);
  const [collectedSources, setCollectedSources] = useState<Source[]>([]);

  // Track previous headers to detect when example data changes
  const prevHeadersRef = useRef<string[]>(data.headers);

  // Reset queries when data changes (e.g., when example is selected)
  useEffect(() => {
    // Check if headers changed (indicates new example was selected)
    const headersChanged = JSON.stringify(prevHeadersRef.current) !== JSON.stringify(data.headers);
    
      if (headersChanged && !isEnrichingTable) {
      // Reset queries when data changes (but not during active enrichment)
      setCollectedQueries([]);
      setCurrentQueries([]);
      setCollectedSources([]);
      setEventHistory([]);
      prevHeadersRef.current = data.headers;
    }
  }, [data.headers, isEnrichingTable]);

  // Add a new row
  const canAddRow = data.rows.length < MAX_ROWS && !isEnrichingTable;
  const canAddColumn = data.headers.length < MAX_COLUMNS && !isEnrichingTable;
  const pendingCellCount = data.rows.reduce(
    (count, row) => count + row.filter((_, colIndex) => isTargetCell(data.headers, row, colIndex)).length,
    0
  );
  const failedCellCount = data.rows.reduce(
    (count, row) => count + row.filter((cell) => cell.status === "failed").length,
    0
  );

  const addRow = () => {
    if (!canAddRow) return;
    const newRows = [...data.rows];
    newRows.push(Array(data.headers.length).fill({ value: "" }));
    setData({ ...data, rows: newRows });
  };

  const [hoverCard, setHoverCard] = useState<{ row: number; col: number; anchor: DOMRect } | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const hideHoverCard = useCallback(() => {
    clearTimeout(hoverTimer.current);
    setHoverCard(null);
  }, []);

  const scheduleHoverCard = (row: number, col: number, element: HTMLElement) => {
    clearTimeout(hoverTimer.current);
    const cell = data.rows[row]?.[col];
    if (!cell || cell.loading || isEditing) return;
    const text = element.querySelector<HTMLElement>("[data-cell-text]");
    // Line boxes overhang by a couple of pixels, so only a hidden half line counts as truncated.
    const truncated = Boolean(text && text.scrollHeight > text.clientHeight + 10);
    if (!cell.enriched && !cell.status && !truncated) {
      setHoverCard(null);
      return;
    }
    hoverTimer.current = setTimeout(
      () => setHoverCard({ row, col, anchor: element.getBoundingClientRect() }),
      hoverCard ? 80 : 350
    );
  };

  const scheduleHoverClose = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoverCard(null), 120);
  };

  const keepHoverCard = () => clearTimeout(hoverTimer.current);

  useEffect(() => {
    if (!hoverCard) return;
    const onScroll = (event: Event) => {
      if (!(event.target instanceof Element && event.target.closest(".cell-card"))) hideHoverCard();
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", hideHoverCard);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", hideHoverCard);
    };
  }, [hoverCard, hideHoverCard]);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const closeClearDialog = useCallback(() => setIsClearDialogOpen(false), []);

  // Clear all table data
  const clearTable = () => {
    setIsClearDialogOpen(false);
    setData({
      headers: Array(5).fill(""),
      rows: Array(5)
        .fill(0)
        .map(() => Array(5).fill({ value: "" })),
    });
    setCollectedQueries([]);
    setCurrentQueries([]);
    setCollectedSources([]);
    setEventHistory([]);
    setToast({
      message: "Table reset",
      type: "success",
      isShowing: true,
    });
  };

  // File upload functions
  const parseCSV = (file: File): Promise<SpreadsheetData> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const data = results.data as Record<string, any>[];
            if (data.length === 0) {
              reject(new Error("CSV file is empty"));
              return;
            }

            const headers = results.meta.fields || Object.keys(data[0]);
            const rows: any[][] = data.map((row) =>
              headers.map((header) => ({
                value: String(row[header] || "").trim(),
                sources: [],
                enriched: false,
                loading: false,
              }))
            );

            resolve({ headers, rows });
          } catch (error) {
            reject(error);
          }
        },
        error: (error) => {
          reject(new Error(`Failed to parse CSV: ${error.message}`));
        },
      });
    });
  };

  const parseExcel = (file: File): Promise<SpreadsheetData> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(data);

          const worksheet = workbook.worksheets[0];

          if (!worksheet) {
            reject(new Error("Excel file is empty"));
            return;
          }

          const jsonData: any[][] = [];
          worksheet.eachRow((row) => {
            const rowValues = row.values as any[];
            jsonData.push(rowValues.slice(1)); // slice(1) to remove the first empty element (ExcelJS rows are 1-indexed)
          });

          if (jsonData.length === 0) {
            reject(new Error("Excel file is empty"));
            return;
          }

          const headers = jsonData[0].map((h) => String(h || "").trim());

          if (headers.length === 0 || headers.every(h => !h)) {
            reject(new Error("Excel file has no valid headers"));
            return;
          }

          const rows: any[][] = jsonData.length > 1
            ? jsonData.slice(1).map((row) =>
                headers.map((_, index) => ({
                  value: String(row[index] || "").trim(),
                  sources: [],
                  enriched: false,
                  loading: false,
                }))
              )
            : [];

          resolve({ headers, rows });
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error instanceof Error ? error.message : "Unknown error"}`));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !['csv', 'xlsx', 'xls'].includes(fileExtension)) {
      setToast({
        message: "Please upload a CSV, XLSX, or XLS file",
        type: "error",
        isShowing: true,
      });
      return;
    }

    try {
      setToast({
        message: "Parsing file...",
        type: "info",
        isShowing: true,
      });

      let parsedData: SpreadsheetData;

      if (fileExtension === 'csv') {
        parsedData = await parseCSV(file);
      } else {
        parsedData = await parseExcel(file);
      }

      const truncated = exceedsLimits(parsedData);
      setData(limitTable(parsedData));
      setCollectedQueries([]);
      setCurrentQueries([]);
      setEventHistory([]);

      setToast(
        truncated
          ? {
              message: `Loaded the first ${MAX_ROWS} rows and ${MAX_COLUMNS} columns. This demo enriches up to that size at a time.`,
              type: "info",
              isShowing: true,
            }
          : {
              message: "File uploaded successfully!",
              type: "success",
              isShowing: true,
            }
      );
    } catch (error) {
      console.error("Error parsing file:", error);
      setToast({
        message: error instanceof Error ? error.message : "Failed to parse file",
        type: "error",
        isShowing: true,
      });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Add a new column
  const addColumn = () => {
    if (!canAddColumn) return;
    const newHeaders = [...data.headers, ""];
    const newRows = data.rows.map((row) => [...row, { value: "" }]);
    setData({ headers: newHeaders, rows: newRows });
  };

  // Delete a column
  const deleteColumn = (colIndex: number) => {
    if (data.headers.length <= 1) return; // Prevent deleting the last column

    const newHeaders = [...data.headers];
    newHeaders.splice(colIndex, 1);

    const newRows = data.rows.map((row) => {
      const newRow = [...row];
      newRow.splice(colIndex, 1);
      return newRow;
    });

    setData({ headers: newHeaders, rows: newRows });
  };

  // Focus on cell
  const focusCell = (row: number, col: number) => {
    setActiveCell({ row, col });
    setEditValue(data.rows[row][col].value);
    setIsEditing(true);
  };

  // Handle cell change
  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  // Save cell value
  const saveCell = () => {
    if (activeCell) {
      const { row, col } = activeCell;
      if (editValue !== data.rows[row][col].value) {
        const newRows = [...data.rows];
        newRows[row] = [...newRows[row]];
        newRows[row][col] = { value: editValue, sources: [] };
        setData({ ...data, rows: newRows });
      }
      setIsEditing(false);
      setActiveCell(null);
    }
  };

  // Handle key events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isEditing) {
      if (e.key === "Enter") {
        saveCell();
      } else if (e.key === "Escape") {
        setIsEditing(false);
        setActiveCell(null);
      }
    }
  };

  const enrichTable = async () => {
    if (!data.headers[0]?.trim()) {
      setToast({
        message: "Please set the first column header",
        type: "error",
        isShowing: true,
      });
      return;
    }

    if (!data.rows.map((row) => row[0]).some((cell) => cell?.value?.length)) {
      setToast({
        message: "Please specify a key in the first column",
        type: "error",
        isShowing: true,
      });
      return;
    }

    if (data.rows.length > MAX_ROWS || data.headers.length > MAX_COLUMNS) {
      setToast({
        message: `This demo enriches up to ${MAX_ROWS} rows and ${MAX_COLUMNS} columns at a time`,
        type: "error",
        isShowing: true,
      });
      return;
    }

    if (pendingCellCount === 0) {
      setToast({
        message: "Every cell already has a value. Clear a cell or add a column to enrich more.",
        type: "info",
        isShowing: true,
      });
      return;
    }

    // Only empty cells are sent for research; populated cells are never overwritten.
    const headers = data.headers;
    const rows = data.rows.map((row) => row.map((cell) => cell.value || ""));
    const contextColumn = headers.find((_, idx) =>
      data.rows.some((row) => row[idx]?.value?.trim())
    );

    setData((prev) => ({
      ...prev,
      rows: prev.rows.map((row) =>
        row.map((cell, colIndex) =>
          isTargetCell(prev.headers, row, colIndex) ? { value: "", sources: [], loading: true } : cell
        )
      ),
    }));
    setIsEnrichingTable(true);
    setEnrichmentProgress("Starting research...");
    setRowProgress({ done: 0, total: 0 });
    setCollectedQueries([]); // Reset queries for new enrichment
    setCurrentQueries([]);
    setCollectedSources([]); // Reset sources for new enrichment
    setEventHistory([{ type: "start", message: "Starting research...", timestamp: Date.now(), completed: false }]);

    const controller = new AbortController();
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => controller.abort(), IDLE_TIMEOUT_MS);
    };
    let outcome: StreamOutcome = null;
    let cellError = "Interrupted before this cell finished";

    try {
      resetIdleTimer();
      const response = await fetch(`${API_URL}/api/enrich/table-wide/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          headers: headers,
          rows: rows,
          context_column: contextColumn,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail =
          typeof errorData.detail === "string" ? errorData.detail : errorData.error;
        throw new Error(detail || response.statusText || "Request failed");
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      try {
        while (!outcome) {
          const { done, value } = await reader.read();
          if (done) break;
          resetIdleTimer();

          buffer += decoder.decode(value, { stream: true });
          // Process complete SSE messages (lines ending with \n)
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let eventData;
            try {
              eventData = JSON.parse(line.slice(6));
            } catch (parseError) {
              console.error("Error parsing SSE data:", parseError, line);
              continue;
            }
            const result = handleStreamEvent(eventData, headers);
            if (result === "error") cellError = eventData.message || "Enrichment failed";
            if (result) outcome = result;
          }
        }
      } finally {
        await reader.cancel().catch(() => {});
      }

      if (!outcome) {
        throw new Error(`The connection ended before enrichment finished. ${RETRY_HINT}`);
      }
    } catch (error) {
      console.error("Error during table enrichment:", error);
      const timedOut = error instanceof Error && error.name === "AbortError";
      setToast({
        message: timedOut
          ? `Enrichment stopped responding. ${RETRY_HINT}`
          : error instanceof Error
            ? error.message
            : "Table enrichment failed",
        type: "error",
        isShowing: true,
      });
      setEventHistory([]);
    } finally {
      clearTimeout(idleTimer);
      // Cells still loading never received a result, so mark them retryable.
      setData((prev) => ({
        ...prev,
        rows: prev.rows.map((row) =>
          row.map((cell) =>
            cell.loading ? { value: "", status: "failed", error: cellError } : cell
          )
        ),
      }));
      setIsEnrichingTable(false);
      setEnrichmentProgress("");
      setCurrentQueries([]);
    }
  };

  const handleStreamEvent = (
    event: any,
    headers: string[]
  ): StreamOutcome => {
    switch (event.type) {
      case "start":
        setRowProgress({ done: 0, total: event.total_rows || 0 });
        setEnrichmentProgress(event.message || "Starting research...");
        setEventHistory((prev) => [...prev, { type: "start", message: event.message || "Starting research...", timestamp: Date.now(), completed: true }]);
        break;

      case "progress":
        setEnrichmentProgress(event.message || "Processing...");
        break;

      case "tool_call":
        if (event.tool === "WebSearch" && event.queries) {
          // Collect queries
          const newQueries = Array.isArray(event.queries) ? event.queries : [];
          setCollectedQueries((prev) => [...prev, ...newQueries]);
          // Accumulate queries from all batches instead of replacing
          setCurrentQueries((prev) => {
            // Combine previous and new queries, removing duplicates
            const combined = [...prev, ...newQueries];
            // Remove duplicates based on query text
            const unique = combined.filter((query, index, self) => 
              self.findIndex(q => q === query) === index
            );
            return unique;
          });
          
          // Collect sources if available - check multiple possible locations
          const sourcesToAdd: Source[] = [];
          
          // Check event.sources (direct)
          if (event.sources && Array.isArray(event.sources)) {
            sourcesToAdd.push(...event.sources);
          }
          
          // Check event.reasoning_steps for sources
          if (event.reasoning_steps && Array.isArray(event.reasoning_steps)) {
            event.reasoning_steps.forEach((step: any) => {
              if (step.sources && Array.isArray(step.sources)) {
                sourcesToAdd.push(...step.sources);
              }
              // Check sub-steps
              if (step.sub_steps && Array.isArray(step.sub_steps)) {
                step.sub_steps.forEach((subStep: any) => {
                  if (subStep.sources && Array.isArray(subStep.sources)) {
                    sourcesToAdd.push(...subStep.sources);
                  }
                });
              }
            });
          }
          
          // Add unique sources
          if (sourcesToAdd.length > 0) {
            setCollectedSources((prev) => {
              const seenUrls = new Set(prev.map(s => s.url));
              const newSources = sourcesToAdd.filter((s: Source) => s && s.url && !seenUrls.has(s.url));
              return [...prev, ...newSources];
            });
          }
          
          const message = `Searching the web (${newQueries.length} ${newQueries.length === 1 ? "query" : "queries"})...`;
          setEnrichmentProgress(message);
          setEventHistory((prev) => {
            // Mark previous active events as completed
            const updated = prev.map(e => e.completed ? e : { ...e, completed: true });
            return [...updated, { type: "search", message, timestamp: Date.now(), completed: false }];
          });
        } else if (event.tool === "Planning") {
          setEnrichmentProgress("Planning research strategy...");
          setCurrentQueries([]);
          setEventHistory((prev) => {
            // Only add Planning if it doesn't already exist (keep first one)
            const hasPlanning = prev.some(e => e.type === "planning");
            if (hasPlanning) {
              return prev; // Don't add duplicate Planning
            }
            const updated = prev.map(e => e.completed ? e : { ...e, completed: true });
            return [...updated, { type: "planning", message: "Planning research strategy...", timestamp: Date.now(), completed: false }];
          });
        } else if (event.tool === "Generating") {
          setEnrichmentProgress("Generating research report...");
          setCurrentQueries([]);
          // Don't add "Generating" to eventHistory - user doesn't want it in Research Progress
        } else if (event.tool === "ResearchSubtopic") {
          setEnrichmentProgress("Researching subtopics...");
          setCurrentQueries([]);
          setEventHistory((prev) => {
            const updated = prev.map(e => e.completed ? e : { ...e, completed: true });
            return [...updated, { type: "research", message: "Researching subtopics...", timestamp: Date.now(), completed: false }];
          });
        } else {
          setEnrichmentProgress(`Executing ${event.tool || "tool"}...`);
          setCurrentQueries([]);
        }
        break;

      case "sources_found":
        // Collect sources in real-time from the event
        if (event.sources && Array.isArray(event.sources)) {
          setCollectedSources((prev) => {
            const seenUrls = new Set(prev.map(s => s.url));
            const newSources = event.sources.filter((s: Source) => s && s.url && !seenUrls.has(s.url));
            return [...prev, ...newSources];
          });
          // Update progress message with source count
          setEnrichmentProgress(`Found ${event.count || event.sources.length} sources from ${event.tool || "research"}...`);
        }
        break;

      case "sources_complete":
        // Accumulate sources from complete event (don't replace, just add new ones)
        if (event.sources && Array.isArray(event.sources)) {
          setCollectedSources((prev) => {
            const seenUrls = new Set(prev.map(s => s.url));
            const newSources = event.sources.filter((s: Source) => s && s.url && !seenUrls.has(s.url));
            const updated = [...prev, ...newSources];
            // Update progress message with total count
            const totalCount = event.count || updated.length;
            setEnrichmentProgress(`Research complete: ${totalCount} total sources`);
            return updated;
          });
        } else {
          setEnrichmentProgress(`Research complete: ${event.count || collectedSources.length} total sources`);
        }
        break;

      case "content_chunk":
        // Optionally show content generation progress
        setEnrichmentProgress("Generating content...");
        break;

      case "row_result": {
        const values: Record<string, string> = event.values || {};
        const failed: string[] = event.failed || [];
        const notFound: string[] = event.not_found || [];
        const rowSources: Source[] = Array.isArray(event.sources) ? event.sources : [];
        setData((prev) => ({
          ...prev,
          rows: prev.rows.map((row, rowIndex) =>
            rowIndex !== event.row
              ? row
              : row.map((cell, colIndex) => {
                  if (!cell.loading) return cell;
                  const header = headers[colIndex];
                  if (Object.prototype.hasOwnProperty.call(values, header)) {
                    return { value: String(values[header]), sources: rowSources, enriched: true };
                  }
                  if (failed.includes(header)) {
                    return { value: "", status: "failed", error: event.error || "Research failed" };
                  }
                  if (notFound.includes(header)) {
                    return { value: "", status: "not_found" };
                  }
                  return cell;
                })
          ),
        }));
        if (rowSources.length > 0) {
          setCollectedSources((prev) => {
            const seenUrls = new Set(prev.map((s) => s.url));
            return [...prev, ...rowSources.filter((s) => s.url && !seenUrls.has(s.url))];
          });
        }
        setRowProgress((prev) => ({ ...prev, done: prev.done + 1 }));
        return null;
      }

      case "complete": {
        const filled = event.filled ?? 0;
        const failed = event.failed ?? 0;
        const notFound = event.not_found ?? 0;
        const filledText = `Filled ${cellCount(filled)}.`;
        if (failed > 0) {
          setToast({
            message: `${filled > 0 ? `${filledText} ` : ""}${cellCount(failed)} failed. ${RETRY_HINT}`,
            type: "error",
            isShowing: true,
          });
        } else if (notFound > 0) {
          setToast({
            message: `${filledText} No answer found for ${cellCount(notFound)}; run again to retry them.`,
            type: "info",
            isShowing: true,
          });
        } else {
          setToast({ message: `Table enriched. ${filledText}`, type: "success", isShowing: true });
        }
        setEventHistory((prev) => {
          const completed = prev.map(e => ({ ...e, completed: true }));
          return [...completed, { type: "complete", message: "Enrichment complete!", timestamp: Date.now(), completed: true }];
        });
        // Clear event history after a delay
        setTimeout(() => {
          setEventHistory([]);
          setCollectedSources([]);
        }, 2000);
        return "complete";
      }

      case "error":
        setToast({
          message: event.message || "Enrichment failed",
          type: "error",
          isShowing: true,
        });
        return "error";

      default:
        // Unknown event type, log for debugging
        console.log("Unknown event type:", event.type, event);
    }
    return null;
  };

  // Edit column header
  const [editingHeader, setEditingHeader] = useState<number | null>(null);
  const [headerEditValue, setHeaderEditValue] = useState("");
  const headerInputRef = useRef<HTMLInputElement>(null);

  const startEditingHeader = (index: number) => {
    setEditingHeader(index);
    setHeaderEditValue(data.headers[index]);
  };

  const saveHeaderEdit = () => {
    if (editingHeader !== null) {
      const newHeaders = [...data.headers];
      newHeaders[editingHeader] = headerEditValue;
      setData({ ...data, headers: newHeaders });
      setEditingHeader(null);
    }
  };

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      saveHeaderEdit();
    } else if (e.key === "Escape") {
      setEditingHeader(null);
    }
  };

  // Focus the input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Focus header input when editing header
  useEffect(() => {
    if (editingHeader !== null && headerInputRef.current) {
      headerInputRef.current.focus();
    }
  }, [editingHeader]);

  // Collect all sources from all cells and deduplicate by URL
  const getAllSources = (): Source[] => {
    const allSources: Source[] = [];
    const seenUrls = new Set<string>();
    
    data.rows.forEach((row) => {
      row.forEach((cell) => {
        if (cell.sources && Array.isArray(cell.sources) && cell.sources.length > 0) {
          // Filter out any invalid sources (must have url) and deduplicate
          cell.sources.forEach((source) => {
            if (source && source.url && typeof source.url === "string") {
              // Only add if we haven't seen this URL before
              if (!seenUrls.has(source.url)) {
                seenUrls.add(source.url);
                allSources.push(source);
              }
            }
          });
        }
      });
    });
    return allSources;
  };

  const allSources = getAllSources();
  const hasSources = allSources.length > 0;
  
  // Get unique queries
  const uniqueQueries = Array.from(new Set(collectedQueries));
  const hasQueries = uniqueQueries.length > 0;

  return (
    <motion.div
      className="w-full mb-40 relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ marginTop: "25px" }}
    >
      {/* Loading overlay for table-wide enrichment */}
      {isEnrichingTable && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "#1d2a3640", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}>
          <motion.div
            className="rounded-[16px] w-full max-w-5xl mx-4"
            style={{
              // Near-opaque: the blurred backdrop above is a backdrop root, so
              // this panel can't blur the page itself.
              background: "#f4f6f8f7",
              border: "1px solid var(--glass-rim)",
              maxHeight: "85vh",
              boxShadow: "0 20px 80px #1d334a33"
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col p-6 sm:p-8 h-full">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4 flex-shrink-0">
                <div className="w-10 h-10 border-4 rounded-full animate-spin flex-shrink-0" style={{
                  borderColor: "var(--ink)",
                  borderTopColor: "transparent"
                }}></div>
                <div className="flex-1">
                  <p className="text-lg font-medium" style={{ color: "var(--color-black)" }}>
                    Enriching entire table...
                  </p>
                  <p className="text-sm" style={{ color: "var(--color-black-60)" }}>
                    {enrichmentProgress || "Processing..."}
                  </p>
                  {rowProgress.total > 0 && (
                    <p className="text-[11px] mt-1 tabular-nums" style={{ color: "var(--muted)", fontFamily: "var(--mono)" }}>
                      {rowProgress.done} of {rowProgress.total} {rowProgress.total === 1 ? "row" : "rows"} finished
                    </p>
                  )}
                </div>
              </div>

              {/* Favicons at the top - Max 2 rows */}
              {(() => {
                const sourcesWithFavicons = collectedSources.filter(s => s.favicon && s.favicon !== null);
                // Estimate: favicon (24px) + gap (8px) = 32px per item
                // Container width ~400px, so ~12 per row = 24 total for 2 rows
                const maxFavicons = 24;
                const visibleFavicons = sourcesWithFavicons.slice(0, maxFavicons);
                const remainingCount = sourcesWithFavicons.length - maxFavicons;
                
                return sourcesWithFavicons.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b flex-shrink-0" style={{ 
                    borderColor: "var(--color-black-10)",
                    maxHeight: "52px", // 2 rows: (24px favicon + 8px gap) * 2 = 64px, but using 52px for tighter spacing
                    overflow: "hidden"
                  }}>
                    {visibleFavicons.map((source, idx) => (
                      <motion.a
                        key={source.url || idx}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.02 }}
                        className="flex items-center justify-center w-6 h-6 rounded transition-colors flex-shrink-0"
                        style={{
                          background: "var(--inset)",
                          border: "1px solid var(--glass-rim)"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "var(--color-black-20)";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "var(--inset)";
                          e.currentTarget.style.borderColor = "var(--glass-rim)";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                        title={source.title || source.url}
                      >
                        <img
                          src={source.favicon!}
                          alt=""
                          className="w-4 h-4 rounded-sm"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      </motion.a>
                    ))}
                    {remainingCount > 0 && (
                      <div className="flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded flex-shrink-0" style={{
                        background: "var(--inset)",
                        border: "1px solid var(--glass-rim)"
                      }}>
                        <span className="text-[11px] font-medium whitespace-nowrap" style={{ color: "var(--muted)", fontFamily: "var(--mono)" }}>
                          +{remainingCount}
                        </span>
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
              
              {/* Two-column layout: Research Progress (1/3 left), Active Queries (2/3 right) - Fixed sizes with scrolling */}
              <div className="flex flex-col sm:flex-row gap-4" style={{ height: "60vh" }}>
                {/* Left: Research Progress - 1/3 width */}
                <div className="flex-[1] flex flex-col rounded-xl border overflow-hidden" style={{
                  background: "var(--glass-strong)",
                  borderColor: "var(--glass-rim)",
                  minWidth: 0,
                  height: "100%"
                }}>
                  <div className="px-4 py-3 border-b flex-shrink-0" style={{
                    borderColor: "var(--border)",
                    background: "var(--inset)"
                  }}>
                    <p className="eyebrow">
                      Research progress
                    </p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0, maxHeight: "100%" }}>
                    <div className="space-y-3">
                      {eventHistory.length > 0 ? (
                        eventHistory.map((event, index) => {
                          const getEventIcon = () => {
                            if (event.completed) {
                              return <CheckCircleIcon className="w-4 h-4" weight="fill" style={{ color: "#1f7a4d" }} />;
                            }
                            return <CircleNotchIcon className="w-4 h-4 animate-spin" style={{ color: "var(--ink)" }} />;
                          };

                          const getEventLabel = () => {
                            switch (event.type) {
                              case "start": return "Started";
                              case "planning": return "Planning";
                              case "search": return "Searching";
                              case "research": return "Researching";
                              case "generating": return "Generating";
                              case "complete": return "Complete";
                              default: return "Processing";
                            }
                          };

                          return (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="flex items-start gap-3"
                            >
                              <div className="flex-shrink-0 mt-0.5">
                                {getEventIcon()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-sm font-medium" style={{ color: "var(--color-black)" }}>
                                    {getEventLabel()}
                                  </span>
                                </div>
                                <p className="text-[11px] leading-relaxed" style={{ color: "var(--muted)", fontFamily: "var(--mono)" }}>
                                  {event.message}
                                </p>
                              </div>
                            </motion.div>
                          );
                        })
                      ) : (
                        <p className="text-sm" style={{ color: "var(--color-black-40)" }}>
                          Waiting for events...
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Active Queries - 2/3 width, Always visible */}
                <div className="flex-[2] flex flex-col rounded-xl border overflow-hidden" style={{
                  background: "var(--glass-strong)",
                  borderColor: "var(--glass-rim)",
                  minWidth: 0,
                  height: "100%"
                }}>
                  <div className="px-4 py-3 border-b flex-shrink-0" style={{
                    borderColor: "var(--border)",
                    background: "var(--inset)"
                  }}>
                    <div className="flex items-center gap-2">
                      <MagnifyingGlassIcon className="w-4 h-4" style={{ color: "var(--muted)" }} />
                      <span className="eyebrow">
                        Active queries
                      </span>
                      {currentQueries.length > 0 && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full" style={{
                          background: "#ffffffb3",
                          border: "1px solid var(--glass-rim)",
                          color: "var(--ink)",
                          fontFamily: "var(--mono)"
                        }}>
                          {currentQueries.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0, maxHeight: "100%" }}>
                    {currentQueries.length > 0 ? (
                      <div className="space-y-2">
                        {currentQueries.map((query, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="flex items-start gap-2 p-3 rounded-lg"
                            style={{
                              background: "var(--inset)",
                              border: "1px solid var(--glass-rim)"
                            }}
                          >
                            <LightningIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "var(--muted)" }} />
                            <p className="text-xs leading-relaxed flex-1" style={{ color: "var(--color-black)" }}>
                              {query}
                            </p>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: "var(--color-black-40)" }}>
                        No active queries yet...
                      </p>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </motion.div>
        </div>
      )}
      {/* Unified Sources Panel */}
      <UnifiedSourcesPanel
        sources={allSources}
        isOpen={isSourcesPanelOpen}
        onClose={() => setIsSourcesPanelOpen(false)}
      />

      {hoverCard && data.rows[hoverCard.row]?.[hoverCard.col] && !isEditing && (
        <CellHoverCard
          anchor={hoverCard.anchor}
          header={data.headers[hoverCard.col] ?? ""}
          cell={data.rows[hoverCard.row][hoverCard.col]}
          onMouseEnter={keepHoverCard}
          onMouseLeave={scheduleHoverClose}
        />
      )}

      <ConfirmDialog
        isOpen={isClearDialogOpen}
        title="Reset the table?"
        message="This removes every row, column, and enriched value. Save the table first if you want a copy."
        confirmLabel="Reset table"
        onConfirm={clearTable}
        onCancel={closeClearDialog}
      />

      {/* Table at the top */}
      <div className="w-full mb-6 glass rounded-[16px] p-3 sm:p-4">
        <div className="sheet-frame">
          <table
            ref={tableRef}
            className="enrich-table"
            style={{ minWidth: ROW_INDEX_WIDTH + data.headers.length * MIN_COLUMN_WIDTH }}
          >
          <thead>
            <tr>
              <th className="row-index" scope="col">
                <span className="sr-only">Row</span>
              </th>
              {data.headers.map((header, index) => (
                <th key={index} scope="col">
                  <div className="header-cell">
                    {editingHeader === index ? (
                      <input
                        ref={headerInputRef}
                        type="text"
                        value={headerEditValue}
                        onChange={(e) => setHeaderEditValue(e.target.value)}
                        onBlur={saveHeaderEdit}
                        onKeyDown={handleHeaderKeyDown}
                        className="header-input"
                        placeholder="Column name"
                        aria-label={`Column ${index + 1} name`}
                      />
                    ) : (
                      <button
                        type="button"
                        className="header-label"
                        onClick={() => startEditingHeader(index)}
                        title={header ? `${header} (click to rename)` : "Click to name this column"}
                      >
                        <span className={header ? undefined : "header-placeholder"}>
                          {header || (index === 0 ? "Entity" : `Column ${index + 1}`)}
                        </span>
                      </button>
                    )}
                    {index !== 0 && editingHeader !== index && (
                      <button
                        type="button"
                        className="header-delete"
                        onClick={() => deleteColumn(index)}
                        disabled={isEnrichingTable}
                        title="Delete column"
                        aria-label={`Delete column ${header || index + 1}`}
                      >
                        <TrashIcon size={13} />
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td className="row-index">{rowIndex + 1}</td>
                {row.map((cell, colIndex) => (
                  <motion.td
                    key={colIndex}
                    className={
                      activeCell?.row === rowIndex && activeCell?.col === colIndex && isEditing
                        ? "is-active"
                        : undefined
                    }
                    onClick={() => {
                      hideHoverCard();
                      focusCell(rowIndex, colIndex);
                    }}
                    onMouseEnter={(event) => scheduleHoverCard(rowIndex, colIndex, event.currentTarget)}
                    onMouseLeave={scheduleHoverClose}
                    data-enriched={cell.enriched ? "true" : "false"}
                  >
                    {activeCell?.row === rowIndex &&
                    activeCell?.col === colIndex &&
                    isEditing ? (
                      <input
                        ref={inputRef}
                        type="text"
                        value={editValue}
                        onChange={handleCellChange}
                        onBlur={saveCell}
                        onKeyDown={handleKeyDown}
                        className="w-full h-full p-0 border-0 outline-none bg-transparent text-sm leading-5 focus:outline-none focus:ring-0 focus:border-transparent"
                      />
                    ) : (
                      <motion.div
                        className="w-full min-h-6 flex items-start"
                        initial={
                          cell.enriched ? { opacity: 0 } : { opacity: 1 }
                        }
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        style={{
                          maxHeight: "7.5rem", // ~6 lines at 1.25rem line-height
                          overflow: "hidden"
                        }}
                      >
                        {cell.loading ? (
                          <div className="flex items-center justify-left w-full">
                            <div className="w-4 h-4 border-2 rounded-full animate-spin flex-shrink-0" style={{
                              borderColor: "var(--ink)",
                              borderTopColor: "transparent"
                            }}></div>
                            <span className="ml-2 text-[11px]" style={{ color: "var(--muted)", fontFamily: "var(--mono)" }}>
                              Researching... (10-30s)
                            </span>
                          </div>
                        ) : cell.status === "failed" ? (
                          <span className="cell-status is-failed">
                            <WarningCircleIcon size={13} aria-hidden="true" />
                            Failed
                          </span>
                        ) : cell.status === "not_found" ? (
                          <span className="cell-status">Not found</span>
                        ) : (
                          <>
                            <span
                              data-cell-text
                              className="w-full text-sm leading-5"
                              style={{
                                color: "var(--ink)",
                                fontWeight: cell.enriched ? 500 : 400,
                                display: "-webkit-box",
                                WebkitLineClamp: 6,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                wordBreak: "break-word"
                              }}
                            >
                              {cell.value}
                            </span>
                          </>
                        )}
                      </motion.div>
                    )}
                  </motion.td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="sheet-toolbar">
          <div className="sheet-toolbar-group">
            <button
              type="button"
              className="tool-button"
              onClick={addRow}
              disabled={!canAddRow}
              title={data.rows.length >= MAX_ROWS ? `Up to ${MAX_ROWS} rows` : "Add row"}
            >
              <PlusIcon size={14} />
              Row
            </button>
            <button
              type="button"
              className="tool-button"
              onClick={addColumn}
              disabled={!canAddColumn}
              title={data.headers.length >= MAX_COLUMNS ? `Up to ${MAX_COLUMNS} columns` : "Add column"}
            >
              <PlusIcon size={14} />
              Column
            </button>
            <span className="sheet-count">
              {data.rows.length}/{MAX_ROWS} rows · {data.headers.length}/{MAX_COLUMNS} columns
            </span>
          </div>

          <div className="sheet-toolbar-primary">
            <button
              type="button"
              className="btn-pill"
              disabled={isEnrichingTable}
              onClick={enrichTable}
            >
              {failedCellCount > 0 && !isEnrichingTable ? <ArrowClockwiseIcon size={15} /> : <SparkleIcon size={15} />}
              <span>
                {isEnrichingTable
                  ? "Enriching..."
                  : failedCellCount > 0
                    ? `Retry failed (${failedCellCount})`
                    : "Enrich table"}
              </span>
            </button>
          </div>

          <div className="sheet-toolbar-group justify-end">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              aria-label="Upload file"
            />
            <button
              type="button"
              className="tool-button icon"
              onClick={handleUploadClick}
              disabled={isEnrichingTable}
              data-tip="Upload"
              aria-label="Upload CSV or Excel"
            >
              <UploadSimpleIcon size={15} />
            </button>
            <button
              type="button"
              className="tool-button icon"
              onClick={() => exportToCSV(data)}
              data-tip="Save"
              aria-label="Save as CSV"
            >
              <DownloadSimpleIcon size={15} />
            </button>
            <button
              type="button"
              className="tool-button icon danger"
              onClick={() => setIsClearDialogOpen(true)}
              disabled={isEnrichingTable}
              data-tip="Reset"
              aria-label="Reset table"
            >
              <TrashIcon size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Controls below the table */}
      <div className="flex flex-col gap-4 mt-6">
        {/* Example */}
        <div className="flex items-center justify-between gap-4">
          <ExamplePopup visible={true} onExampleSelect={(example) => setData(limitTable(example))} />
        </div>

        {/* Sources section at the bottom - like research page */}
        {hasSources && (
          <div className="mt-6 glass rounded-[16px] p-3 sm:p-4">
            <div className="eyebrow mb-2 sm:mb-3 flex items-center gap-2">
              <span>Sources</span>
              <span style={{ color: "var(--faint)" }}>
                {allSources.length}
              </span>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {allSources.map((source, idx) => (
                <a
                  key={idx}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-2.5 sm:p-3 rounded-[10px] transition active:scale-[0.98]"
                  style={{
                    background: "var(--glass-strong)",
                    border: "1px solid var(--glass-rim)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#ffffffe6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "var(--glass-strong)";
                  }}
                >
                  <div className="flex items-center gap-2">
                    {source.favicon && (
                      <img
                        src={source.favicon}
                        alt=""
                        className="w-4 h-4 flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    <div
                      className="text-sm font-medium line-clamp-1"
                      style={{ color: "var(--ink)" }}
                    >
                      {source.title}
                    </div>
                  </div>
                  {(source as any).snippet && (
                    <div
                      className="text-xs mt-1 line-clamp-2 hidden sm:block"
                      style={{ color: "var(--color-black-60)" }}
                    >
                      {(source as any).snippet}
                    </div>
                  )}
                  <div
                    className="text-[11px] mt-1"
                    style={{ color: "var(--faint)", fontFamily: "var(--mono)" }}
                  >
                    {new URL(source.url).hostname}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Search Queries section at the bottom - like research page */}
        {hasQueries && (
          <div className="mt-6 glass rounded-[16px] p-3 sm:p-4">
            <div className="eyebrow mb-2 sm:mb-3 flex items-center gap-2">
              <span>Search queries</span>
              <span style={{ color: "var(--faint)" }}>
                {uniqueQueries.length}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
              {uniqueQueries.map((query, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 p-2.5 sm:p-3 rounded-[10px] transition"
                  style={{
                    background: "var(--glass-strong)",
                    border: "1px solid var(--glass-rim)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#ffffffe6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "var(--glass-strong)";
                  }}
                >
                  <MagnifyingGlassIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--muted)" }} />
                  <div
                    className="text-sm leading-relaxed flex-1"
                    style={{ color: "var(--color-black)" }}
                  >
                    {query}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Spreadsheet;
